import React, { ReactElement } from 'react'
import { FormikProps, useFormik } from 'formik'
import * as yup from 'yup'
import dayjs from 'dayjs'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import {
  AccreditationV2,
  ANCCAccreditation,
  CAPCEAccreditationV2,
  CEBrokerAccreditationV2,
  NewJerseyAccreditationV2,
  NewYorkAccreditationV2,
  VirginiaAccreditationV2,
  AccreditedAccreditationV2,
  NotAccreditedV2,
} from 'src/v3/core/types'

export type AccreditationType = AccreditationV2['kind']

export type AccreditationsByType = {
  ancc: ANCCAccreditation[]
  capce: CAPCEAccreditationV2[]
  newJersey: NewJerseyAccreditationV2[]
  ceBroker: CEBrokerAccreditationV2[]
  newYork: NewYorkAccreditationV2[]
  virginia: VirginiaAccreditationV2[]
  accredited: AccreditedAccreditationV2[]
  notAccredited: NotAccreditedV2[]
}

export const accreditationDict: Record<AccreditationType, string> = {
  ancc: 'ANCC',
  capce: 'CAPCE',
  ceBroker: 'CE Broker',
  newJersey: 'New Jersey',
  newYork: 'New York',
  virginia: 'Virginia',
  accredited: 'Accredited',
  notAccredited: 'Not Accredited',
}

export const accreditationTypes: AccreditationType[] = [
  'ancc',
  'capce',
  'ceBroker',
  'newJersey',
  'newYork',
  'virginia',
  'accredited',
  'notAccredited',
]

export const anccTargetAudiences = [
  'Registered Nurse',
  'Paramedic',
  'EMT',
  'Respiratory Therapist',
  'Advanced Practice Nurse',
  'Other',
]

export const anccEducationalNeeds = ['Knowledge', 'Skill', 'Practice']

export const capceTopicAreas = [
  'Abdominal Emergencies',
  'Abuse',
  'ACLS',
  'Active Shooter',
  'Acute Coronary Syndromes',
  'Adult Respiratory Emergencies',
  'Advanced Airway',
  'AEMT Refresher',
  'Aeromedical Transport',
  'Allergy and Anaphylaxis',
  'ALS',
  'Altered Mental Status',
  'Ambulance Safety',
  'AMLS',
  'Anatomy and Physiology',
  'APLS',
  'Assessment',
  'Asthma',
  'At Risk Populations',
  'Autism',
  'Basic Airway',
  'Blood Borne Pathogens',
  'BLS',
  'Burns',
  'Capnography',
  'Cardiology',
  'CBRNE',
  'Cervical Injuries',
  'Chest Injuries',
  'CHF',
  'CNS Injury',
  'Communications',
  'Community Paramedicine',
  'Congenital Defects',
  'COPD',
  'Corona Virus',
  'COVID-19',
  'CPAP',
  'CPR',
  'Crew Resource Management',
  'Critical Care',
  'Critical Care Transport',
  'Critical Decision Making',
  'Culture of Safety',
  'Death',
  'Depression',
  'Diabetic Emergencies',
  'Disaster Management',
  'Diving and Hyperbarics',
  'Documentation',
  'Ebola',
  'Educator',
  'EKG Recognition',
  'Electrolyte Imbalance',
  'Emergency Child Birth',
  'Emergency Medical Dispatch (EMD)',
  'Emotional Resilience',
  'EMR Refresher Course',
  'EMS Self Defense',
  'EMT Refresher',
  'Endocrinology',
  'Envenomations',
  'Environmental Emergencies',
  'Ethics',
  'Evidence Based Guidelines',
  'Flight Physiology',
  'Fluid Resuscitation',
  'Fracture Management',
  'Geriatrics',
  'GI and GU Emergencies',
  'Gynecology',
  'Hazardous Materials',
  'Head Injuries',
  'Health and Wellness',
  'Heart Rhythms',
  'Hematology',
  'Hemorrhage Control',
  'Human Trafficking',
  'Hygiene Vaccinations',
  'Hypertension',
  'Immunology',
  'Infection Control',
  'Infectious Disease',
  'Intraosteous Infusion',
  'ITLS',
  'IV Therapy',
  'K9 Officer Care',
  'Leadership',
  'Legal',
  'LVAD',
  'Management Training',
  'Mass Violence',
  'Medical Director',
  'Medical Emergencies',
  'Medication Delivery',
  'MRSA',
  'Neonatology',
  'Neurology',
  'Nephrology',
  'Obesity',
  'Obstetrics',
  'Oncology',
  'Operations',
  'Opioids',
  'OSHA',
  'Oxygenation',
  'Pain Management',
  'PALS',
  'Paramedic Refresher',
  'Pediatric Assessment',
  'Pediatric Cardiac Arrest',
  'Pediatric Emergency Standards',
  'Pediatric Medical Emergencies',
  'Pediatric Respiratory Emergencies',
  'Pediatric Shock',
  'Pediatric Trauma',
  'Pediatrics',
  'Pharmacology',
  'Phlebotomy',
  'PHTLS',
  'Post Resuscitation Care',
  'Psychiatric Emergencies',
  'PTSD',
  'Quality Improvement',
  'Research',
  'Respiratory Emergencies',
  'Resuscitation',
  'Safety',
  'Seizures',
  'Sepsis',
  'Shock',
  'Skills',
  'Special Needs',
  'Special Topics',
  'Spinal Restriction',
  'Sports Injuries',
  'Stroke',
  'Tactical',
  'Terrorism Response',
  'Toxicology',
  'Traffic Incident Management',
  'Trauma',
  'Trauma Triage',
  'Traumatic Brain Injuries',
  'Triage',
  'Ultrasound',
  'Urology',
  'Ventilation',
  'Ventricular Assist Devices',
  'Wilderness Medicine',
]

export const capceClassTypes = [
  'ARV',
  'Cardiac',
  'Critical Care',
  'Education',
  'Medical',
  'OBGYN',
  'Operations',
  'Trauma',
  'Pediatrics',
  'Other',
]

const AccreditationModal = <T,>({
  children,
  open,
  setOpen,
  form,
  name,
}: {
  children: ReactElement | ReactElement[]
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  form: FormikProps<T>
  name: AccreditationType
}): ReactElement => (
  <Dialog
    open={open}
    onClose={() => {
      setOpen(false)
      form.resetForm()
    }}
  >
    <DialogContent>
      <Typography
        variant="h6"
        sx={{
          textAlign: 'center',
        }}
      >
        {accreditationDict[name] !== 'Not Accredited' ? (
          <>Add {accreditationDict[name]} Accreditation</>
        ) : (
          <>Enter Course Code</>
        )}
      </Typography>
      <form onSubmit={form.handleSubmit}>
        {children}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 1,
            mt: 2,
          }}
        >
          <Button sx={{ width: 90 }} variant="contained" type="submit">
            Submit
          </Button>
          <Button
            sx={{ width: 90 }}
            variant="contained"
            color="error"
            onClick={() => {
              setOpen(false)
              form.resetForm()
            }}
          >
            Cancel
          </Button>
        </Box>
      </form>
    </DialogContent>
  </Dialog>
)

export const AnccForm = ({
  onSubmit,
  open,
  setOpen,
}: {
  onSubmit: (accreditation: Partial<ANCCAccreditation>) => void
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}): ReactElement => {
  let form = useFormik({
    validationSchema: yup.object({
      courseCode: yup.string().required(),
      expirationDate: yup.string().required(),
      targetAudience: yup.array().of(yup.string()).required().min(1),
      educationalNeeds: yup.array().of(yup.string()).required().min(1),
      competency: yup.string(),
      professionalSource: yup.string(),
    }),
    initialValues: {
      courseCode: '',
      expirationDate: '',
      targetAudience: [],
      educationalNeeds: [],
      competency: undefined,
      professionalSource: undefined,
    },
    onSubmit: (values, { resetForm }) => {
      onSubmit({
        courseCode: values.courseCode,
        expirationDate: values.expirationDate,
        targetAudience: values.targetAudience,
        educationalNeeds: values.educationalNeeds,
        competency: values.competency,
        professionalSource: values.professionalSource,
      })
      setOpen(false)
      resetForm()
    },
  })

  return (
    <AccreditationModal open={open} setOpen={setOpen} form={form} name="ancc">
      <TextField
        sx={{ mt: 1.5 }}
        fullWidth
        variant="outlined"
        value={form.values.courseCode}
        label="Course Code"
        name="courseCode"
        onChange={form.handleChange}
        error={form.touched.courseCode && !!form.errors.courseCode}
      />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          sx={{ mt: 1.5 }}
          disablePast
          value={dayjs(form.values.expirationDate)}
          label="Expiration Date"
          name="expirationDate"
          onChange={(val) => {
            form.setFieldValue('expirationDate', val ? dayjs(val).format() : '')
          }}
          slotProps={{
            textField: {
              fullWidth: true,
              error:
                !!form.touched.expirationDate && !!form.errors.expirationDate,
            },
          }}
        />
      </LocalizationProvider>
      <FormControl fullWidth variant="outlined" sx={{ mt: 1.5 }}>
        <InputLabel>Target Audience</InputLabel>
        <Select
          multiple
          fullWidth
          value={form.values.targetAudience}
          label="Target Audience"
          name="targetAudience"
          onChange={form.handleChange}
          error={form.touched.targetAudience && !!form.errors.targetAudience}
        >
          {anccTargetAudiences.map((targetAudience) => (
            <MenuItem key={targetAudience} value={targetAudience}>
              {targetAudience}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth variant="outlined" sx={{ mt: 1.5 }}>
        <InputLabel>Underlying Educational Needs</InputLabel>
        <Select
          multiple
          fullWidth
          value={form.values.educationalNeeds}
          label="Underlying Educational Needs"
          name="educationalNeeds"
          onChange={form.handleChange}
          error={
            form.touched.educationalNeeds && !!form.errors.educationalNeeds
          }
        >
          {anccEducationalNeeds.map((educationalNeeds) => (
            <MenuItem key={educationalNeeds} value={educationalNeeds}>
              {educationalNeeds}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        sx={{ mt: 1.5 }}
        minRows={3}
        multiline
        fullWidth
        variant="outlined"
        value={form.values.competency}
        label="Competency"
        name="competency"
        onChange={form.handleChange}
        error={form.touched.competency && !!form.errors.competency}
      />
      <TextField
        sx={{ mt: 1.5 }}
        minRows={3}
        multiline
        fullWidth
        variant="outlined"
        value={form.values.professionalSource}
        label="Professional Source"
        name="professionalSource"
        onChange={form.handleChange}
        error={
          form.touched.professionalSource && !!form.errors.professionalSource
        }
      />
    </AccreditationModal>
  )
}

export const CapceForm = ({
  onSubmit,
  open,
  setOpen,
}: {
  onSubmit: (accreditation: Partial<CAPCEAccreditationV2>) => void
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}): ReactElement => {
  let form = useFormik({
    validationSchema: yup.object({
      courseCode: yup.string().required(),
      expirationDate: yup.string(),
      applicationDate: yup.string(),
      submissionDate: yup.string(),
      topicArea: yup.string().required(),
      classType: yup.string().required(),
    }),
    initialValues: {
      courseCode: '',
      expirationDate: undefined,
      applicationDate: undefined,
      submissionDate: undefined,
      topicArea: '',
      classType: '',
    },
    onSubmit: (values, { resetForm }) => {
      onSubmit({
        courseCode: values.courseCode,
        expirationDate: values.expirationDate,
        applicationDate: values.applicationDate,
        submissionDate: values.submissionDate,
        topicArea: values.topicArea,
        classType: values.classType,
      })
      setOpen(false)
      resetForm()
    },
  })

  return (
    <AccreditationModal open={open} setOpen={setOpen} form={form} name="capce">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TextField
          fullWidth
          variant="standard"
          value={form.values.courseCode}
          label="Course Code"
          name="courseCode"
          onChange={form.handleChange}
          error={form.touched.courseCode && !!form.errors.courseCode}
        />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            sx={{ mt: 1.5 }}
            disablePast
            value={dayjs(form.values.expirationDate)}
            label="Expiration Date"
            name="expirationDate"
            onChange={(val) => {
              form.setFieldValue(
                'expirationDate',
                val ? dayjs(val).format() : '',
              )
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                error:
                  !!form.touched.expirationDate && !!form.errors.expirationDate,
              },
            }}
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            sx={{ mt: 1.5 }}
            disablePast
            value={dayjs(form.values.applicationDate)}
            label="Application Date"
            name="applicationDate"
            onChange={(val) => {
              form.setFieldValue(
                'applicationDate',
                val ? dayjs(val).format() : '',
              )
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                error:
                  !!form.touched.applicationDate &&
                  !!form.errors.applicationDate,
              },
            }}
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            sx={{ mt: 1.5 }}
            disablePast
            value={dayjs(form.values.submissionDate)}
            label="Submission Date"
            name="submissionDate"
            onChange={(val) => {
              form.setFieldValue(
                'submissionDate',
                val ? dayjs(val).format() : '',
              )
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                error:
                  !!form.touched.submissionDate && !!form.errors.submissionDate,
              },
            }}
          />
        </LocalizationProvider>
        <FormControl fullWidth variant="outlined" sx={{ mt: 1.5 }}>
          <InputLabel>Topic Area</InputLabel>
          <Select
            value={form.values.topicArea}
            label="Topic Area"
            name="topicArea"
            onChange={form.handleChange}
            error={form.touched.topicArea && !!form.errors.topicArea}
          >
            {capceTopicAreas.map((topicArea) => (
              <MenuItem key={topicArea} value={topicArea}>
                {topicArea}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth variant="outlined" sx={{ mt: 1.5 }}>
          <InputLabel>Class Type</InputLabel>
          <Select
            value={form.values.classType}
            label="Class Type"
            name="classType"
            onChange={form.handleChange}
            error={form.touched.classType && !!form.errors.classType}
          >
            {capceClassTypes.map((classType) => (
              <MenuItem key={classType} value={classType}>
                {classType}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </AccreditationModal>
  )
}

export const CeBrokerForm = ({
  onSubmit,
  open,
  setOpen,
  exclude,
}: {
  onSubmit: (accreditation: Partial<CEBrokerAccreditationV2>) => void
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  exclude?: string[]
}): ReactElement => {
  let form = useFormik({
    validationSchema: yup.object({
      courseCode: yup.string().required(),
      professionCode: yup.string().required(),
      subjectArea: yup.string().required(),
      state: yup.string().required(),
    }),
    initialValues: {
      courseCode: '',
      professionCode: '',
      subjectArea: '',
      state: '',
    },
    onSubmit: (values, { resetForm }) => {
      onSubmit({
        courseCode: values.courseCode,
        professionCode: values.professionCode,
        subjectArea: values.subjectArea,
        state: values.state,
      })
      setOpen(false)
      resetForm()
    },
  })

  return (
    <AccreditationModal
      open={open}
      setOpen={setOpen}
      form={form}
      name="ceBroker"
    >
      <TextField
        fullWidth
        variant="standard"
        value={form.values.courseCode}
        label="Course Code"
        name="courseCode"
        onChange={form.handleChange}
        error={form.touched.courseCode && !!form.errors.courseCode}
      />
      <TextField
        fullWidth
        variant="standard"
        value={form.values.professionCode}
        label="Profession Code"
        name="professionCode"
        onChange={form.handleChange}
        error={form.touched.professionCode && !!form.errors.professionCode}
      />
      <TextField
        fullWidth
        variant="standard"
        value={form.values.subjectArea}
        label="Subject Area"
        name="subjectArea"
        onChange={form.handleChange}
        error={form.touched.subjectArea && !!form.errors.subjectArea}
      />
      <FormControl fullWidth variant="standard">
        <InputLabel>State</InputLabel>
        <Select
          value={form.values.state}
          label="State"
          name="state"
          onChange={form.handleChange}
          error={form.touched.state && !!form.errors.state}
        >
          {!exclude?.includes('Florida') && (
            <MenuItem value="Florida">Florida</MenuItem>
          )}
          {!exclude?.includes('Kentucky') && (
            <MenuItem value="Kentucky">Kentucky</MenuItem>
          )}
          {!exclude?.includes('Tennessee') && (
            <MenuItem value="Tennessee">Tennessee</MenuItem>
          )}
        </Select>
      </FormControl>
    </AccreditationModal>
  )
}

export const NewJerseyForm = ({
  onSubmit,
  open,
  setOpen,
}: {
  onSubmit: (accreditation: Partial<NewJerseyAccreditationV2>) => void
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}): ReactElement => {
  let form = useFormik({
    validationSchema: yup.object({
      courseCode: yup.string().required(),
    }),
    initialValues: {
      courseCode: '',
    },
    onSubmit: (values) => {
      onSubmit({ courseCode: values.courseCode })
      setOpen(false)
    },
  })

  return (
    <AccreditationModal
      open={open}
      setOpen={setOpen}
      form={form}
      name="newJersey"
    >
      <TextField
        fullWidth
        variant="standard"
        value={form.values.courseCode}
        label="Course Code"
        name="courseCode"
        onChange={form.handleChange}
        error={form.touched.courseCode && !!form.errors.courseCode}
      />
    </AccreditationModal>
  )
}

export const NewYorkForm = ({
  onSubmit,
  open,
  setOpen,
}: {
  onSubmit: (accreditation: Partial<NewYorkAccreditationV2>) => void
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}): ReactElement => {
  let form = useFormik({
    validationSchema: yup.object({
      topicArea: yup.string().required(),
    }),
    initialValues: {
      topicArea: '',
    },
    onSubmit: (values) => {
      onSubmit({ topicArea: values.topicArea })
      setOpen(false)
    },
  })

  return (
    <AccreditationModal
      open={open}
      setOpen={setOpen}
      form={form}
      name="newYork"
    >
      <FormControl fullWidth variant="standard">
        <InputLabel>Topic Area</InputLabel>
        <Select
          value={form.values.topicArea}
          label="Topic Area"
          name="topicArea"
          onChange={form.handleChange}
          error={form.touched.topicArea && !!form.errors.topicArea}
        >
          <MenuItem value="Preparatory">Preparatory</MenuItem>
          <MenuItem value="Airway">Airway</MenuItem>
          <MenuItem value="Pharmacology | Med Admin | Emergency Meds">
            Pharmacology | Med Admin | Emergency Meds
          </MenuItem>
          <MenuItem value="Immunology">Immunology</MenuItem>
          <MenuItem value="Toxicology">Toxicology</MenuItem>
          <MenuItem value="Endocrine">Endocrine</MenuItem>
          <MenuItem value="Neurology">Neurology</MenuItem>
          <MenuItem value="Abdominal | Geni-Renal | GI | Hematology">
            Abdominal | Geni-Renal | GI | Hematology
          </MenuItem>
          <MenuItem value="Respiratory">Respiratory</MenuItem>
          <MenuItem value="Psychiatric">Psychiatric</MenuItem>
          <MenuItem value="Cardiology">Cardiology</MenuItem>
          <MenuItem value="Shock and Resuscitation">
            Shock and Resuscitation
          </MenuItem>
          <MenuItem value="Trauma">Trauma</MenuItem>
          <MenuItem value="Geriatrics">Geriatrics</MenuItem>
          <MenuItem value="OB | Neonate | Pediatrics">
            OB | Neonate | Pediatrics
          </MenuItem>
          <MenuItem value="Special Needs Pt">Special Needs Pt</MenuItem>
          <MenuItem value="EMS Operations">EMS Operations</MenuItem>
          <MenuItem value="Mental Health of EMT">Mental Health of EMT</MenuItem>
          <MenuItem value="Patient Lifting and Moving">
            Patient Lifting and Moving
          </MenuItem>
          <MenuItem value="Pediatric Safe Transport">
            Pediatric Safe Transport
          </MenuItem>
          <MenuItem value="Emergency Vehicle Driver Training">
            Emergency Vehicle Driver Training
          </MenuItem>
        </Select>
      </FormControl>
    </AccreditationModal>
  )
}

export const VirginiaForm = ({
  onSubmit,
  open,
  setOpen,
}: {
  onSubmit: (accreditation: Partial<VirginiaAccreditationV2>) => void
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}): ReactElement => {
  let form = useFormik({
    validationSchema: yup.object({
      topic: yup.string().required(),
      subTopic: yup.string().required(),
      areaNumberALS: yup.string(),
      areaNumberBLS: yup.string(),
    }),
    initialValues: {
      topic: '',
      subTopic: '',
      areaNumberALS: '',
      areaNumberBLS: '',
    },
    onSubmit: (values) => {
      onSubmit({
        topic: values.topic,
        subTopic: values.subTopic,
        areaNumberALS: values.areaNumberALS,
        areaNumberBLS: values.areaNumberBLS,
      })
      setOpen(false)
    },
  })

  return (
    <AccreditationModal
      open={open}
      setOpen={setOpen}
      form={form}
      name="virginia"
    >
      <TextField
        fullWidth
        variant="standard"
        value={form.values.topic}
        label="Topic"
        name="topic"
        onChange={form.handleChange}
        error={form.touched.topic && !!form.errors.topic}
      />
      <TextField
        fullWidth
        variant="standard"
        value={form.values.subTopic}
        label="Sub-Topic"
        name="subTopic"
        onChange={form.handleChange}
        error={form.touched.subTopic && !!form.errors.subTopic}
      />
      <TextField
        fullWidth
        variant="standard"
        value={form.values.areaNumberALS}
        label="Area Number ALS"
        name="areaNumberALS"
        onChange={form.handleChange}
        error={form.touched.areaNumberALS && !!form.errors.areaNumberALS}
      />
      <TextField
        fullWidth
        variant="standard"
        value={form.values.areaNumberBLS}
        label="Area Number BLS"
        name="areaNumberBLS"
        onChange={form.handleChange}
        error={form.touched.areaNumberBLS && !!form.errors.areaNumberBLS}
      />
    </AccreditationModal>
  )
}

export const AccreditedForm = ({
  onSubmit,
  open,
  setOpen,
  validateCourseCode,
}: {
  onSubmit: (accreditation: Partial<AccreditedAccreditationV2>) => void
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  validateCourseCode?: (code: string) => string | undefined
}): ReactElement => {
  let form = useFormik({
    validationSchema: yup.object({
      courseCode: yup.string().required(),
    }),
    validate: (values) => {
      let err = validateCourseCode?.(values.courseCode)
      return err ? { courseCode: err } : {}
    },
    initialValues: {
      courseCode: '',
    },
    onSubmit: (values) => {
      onSubmit({
        courseCode: values.courseCode,
      })
      setOpen(false)
    },
  })

  return (
    <AccreditationModal
      open={open}
      setOpen={setOpen}
      form={form}
      name="accredited"
    >
      <TextField
        fullWidth
        variant="standard"
        value={form.values.courseCode}
        label="Course Code"
        name="courseCode"
        onChange={form.handleChange}
        error={form.touched.courseCode && !!form.errors.courseCode}
        helperText={
          form.touched.courseCode ? form.errors.courseCode : undefined
        }
      />
    </AccreditationModal>
  )
}

export const NotAccreditedForm = ({
  onSubmit,
  open,
  setOpen,
  validateCourseCode,
}: {
  onSubmit: (accreditation: Partial<NotAccreditedV2>) => void
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  validateCourseCode?: (code: string) => string | undefined
}): ReactElement => {
  let form = useFormik({
    validationSchema: yup.object({
      courseCode: yup.string().required(),
    }),
    validate: (values) => {
      let err = validateCourseCode?.(values.courseCode)
      return err ? { courseCode: err } : {}
    },
    initialValues: {
      courseCode: '',
    },
    onSubmit: (values) => {
      onSubmit({
        courseCode: values.courseCode,
      })
      setOpen(false)
    },
  })

  return (
    <AccreditationModal
      open={open}
      setOpen={setOpen}
      form={form}
      name="notAccredited"
    >
      <TextField
        fullWidth
        variant="standard"
        value={form.values.courseCode}
        label="Course Code"
        name="courseCode"
        onChange={form.handleChange}
        error={form.touched.courseCode && !!form.errors.courseCode}
        helperText={
          form.touched.courseCode ? form.errors.courseCode : undefined
        }
      />
    </AccreditationModal>
  )
}
